import { FC, useState } from 'react';

import { Send } from 'lucide-react';
import {
  FacebookContentType,
  FacebookPublishRequest,
  InstagramPublishRequest,
  LinkedInPublishRequest,
  MediaType,
  SocialChannels,
} from '@/types/posts';
import { SocialMediaType } from '@/types/settings.ts';
import { useAxiosErrorHandler } from '@/hooks/common';
import { usePublishToSocialMedia } from '@/hooks/posts';

import { Button } from '@/components/ui/button/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import SuccessInfoPanel from '@/components/common/InfoPanels/SuccessInfoPanel';

interface PublishButtonProps {
  projectId: string;
  postId: string;
  currentFormat: SocialChannels;
  imageUrl?: string;
}

// Helper function to convert SocialChannels to SocialMediaType
const convertToSocialMediaType = (format: SocialChannels): SocialMediaType => {
  switch (format) {
    case SocialChannels.Facebook:
      return SocialMediaType.Facebook;
    case SocialChannels.Instagram:
      return SocialMediaType.Instagram;
    case SocialChannels.LinkedIn:
      return SocialMediaType.Linkedin;
    default:
      return SocialMediaType.Facebook; // Default fallback
  }
};

const PublishButton: FC<PublishButtonProps> = ({
  projectId,
  postId,
  currentFormat,
  imageUrl,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [publishDate, setPublishDate] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Convert SocialChannels to SocialMediaType
  const socialChannel = convertToSocialMediaType(currentFormat);

  const { mutateAsync: publishPost, isPending } = usePublishToSocialMedia();
  const { parseAndShowError } = useAxiosErrorHandler();

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
    setIsSuccess(false);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  // const handleOpenConfirmDialog = () => {
  //   setIsConfirmDialogOpen(true);
  // };

  const handleCloseConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
  };

  const handlePublish = async (isImmediate: boolean = false) => {
    try {
      let publishData;

      // Create the appropriate request body based on the social channel
      switch (socialChannel) {
        case SocialMediaType.Instagram:
          publishData = {
            media_type: MediaType.IMAGE,
            resource_url: imageUrl || '',
          } as InstagramPublishRequest;
          break;
        case SocialMediaType.Facebook:
          publishData = {
            content_type: FacebookContentType.POST,
            // Include content_url only if available
            ...(imageUrl ? { content_url: imageUrl } : {}),
          } as FacebookPublishRequest;
          break;
        case SocialMediaType.Linkedin:
          publishData = {} as LinkedInPublishRequest;
          break;
        default:
          throw new Error(`Unsupported social channel: ${socialChannel}`);
      }

      // Add publish_at date only if scheduling for later
      if (!isImmediate && publishDate) {
        publishData.publish_at = new Date(publishDate).toISOString();
      }

      await publishPost({
        projectId,
        postId,
        socialChannel,
        data: publishData,
      });

      setIsSuccess(true);
      // Close dialog after success
      setTimeout(() => {
        setIsDialogOpen(false);
        setIsConfirmDialogOpen(false);
      }, 3000);
    } catch (error) {
      parseAndShowError(error);
    }
  };

  // Format display name
  const formatDisplayName = (format: string) => {
    return format.charAt(0).toUpperCase() + format.slice(1).toLowerCase();
  };

  return (
    <>
      <div className='flex gap-2'>
        {/* <Button
          size='xs'
          onClick={handleOpenConfirmDialog}
          variant={'outline'}
          className='bg-[rgba(0,0,0,.04)] py-3 px-1 rounded-full'
          type='button'
        >
          <span className='flex gap-1 items-center text-[12px] font-semibold font-jakarta px-3'>
            Post Now <Send className='!h-3' />
          </span>
        </Button> */}

        <Button
          size='xs'
          onClick={handleOpenDialog}
          variant={'outline'}
          className='bg-[rgba(0,0,0,.04)] py-3 px-1 rounded-full'
          type='button'
        >
          <span className='flex gap-1 items-center text-[12px] font-semibold font-jakarta px-3'>
            Schedule Post <Send className='!h-3' />
          </span>
        </Button>
      </div>

      {/* Confirmation Dialog for Immediate Post */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>
              Confirm Post to {formatDisplayName(currentFormat)}
            </DialogTitle>
          </DialogHeader>

          {isSuccess ? (
            <SuccessInfoPanel>
              Your post has been published successfully.
            </SuccessInfoPanel>
          ) : (
            <div className='flex flex-col gap-4'>
              <p className='text-gray-600'>
                Are you sure you want to post this content to{' '}
                {formatDisplayName(currentFormat)} right now?
              </p>
              <div className='flex justify-end gap-2'>
                <Button variant='outline' onClick={handleCloseConfirmDialog}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handlePublish(true)}
                  disabled={isPending}
                >
                  {isPending ? 'Posting...' : 'Confirm Post'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>
              Schedule to {formatDisplayName(currentFormat)}
            </DialogTitle>
          </DialogHeader>

          {isSuccess ? (
            <SuccessInfoPanel>
              Your post has been scheduled for publishing.
            </SuccessInfoPanel>
          ) : (
            <div className='flex flex-col gap-4'>
              <div className='flex flex-col gap-2'>
                <label htmlFor='publish-date'>
                  Schedule for later (optional)
                </label>
                <Input
                  id='publish-date'
                  type='datetime-local'
                  value={publishDate}
                  onChange={(e) => setPublishDate(e.target.value)}
                />
              </div>

              <div className='flex justify-end gap-2'>
                <Button variant='outline' onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handlePublish(false)}
                  disabled={isPending}
                >
                  {isPending ? 'Scheduling...' : 'Schedule Post'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PublishButton;
